import {useReducer, useLayoutEffect, useMemo, useCallback, useRef} from 'react';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'flexsearch/dist/flexsearch.es5'. '/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/node_modules/flexsearch/dist/flexsearch.es5.js' implicitly has an 'any' type.
import FlexSearch from 'flexsearch/dist/flexsearch.es5';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'flexsearch7/dist/flexsearch.es5'. '/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/node_modules/flexsearch/dist/flexsearch.es5.js' implicitly has an 'any' type.
import * as fs7 from 'flexsearch7/dist/flexsearch.es5.min';
import {once, weakMemo} from '@packages/utilities/memo';
import {initialState, useFuzzySearchReducer as reducer} from './reducer';
import {actionTypes} from './actions';

import {
  type UseFuzzySearchProps,
  type UseFuzzySearchReturn,
  type ItemActions,
  type Action,
  type Matches,
  FLEX_SEARCH_VERSION,
} from './types';

export {FLEX_SEARCH_VERSION};

import {flushSync} from 'react-dom';

/**
 * A custom React hook that provides fuzzy search capabilities over a list of items.
 *
 * This hook leverages the FlexSearch library to perform efficient fuzzy searches on a collection of items.
 * It maintains internal state to manage the search query, selected and preselected items, and provides handlers
 * for common search interactions such as focus, blur, change, and keyboard navigation.
 *
 * @template Value - The type of the items in the original list.
 * @template FoundValue - The type of the items returned in the search results. Defaults to `Value`.
 *
 * @param {UseFuzzySearchProps<Value, FoundValue>} props - The properties for configuring the hook.
 *
 * @param {Iterable<Value>} props.items - **Required.** The list of items to search over. Can be any iterable collection like an array or set.
 * @param {(value: Value) => string} props.getSearchableText - **Required.** A function that extracts the searchable text from each item. This text is used to build the search index.
 * @param {string} [props.query] - An optional controlled query string. If provided, the hook uses this instead of its internal state. Should be used with `onQueryChange`.
 * @param {(query: string) => void} [props.onQueryChange] - An optional handler called when the query changes. Should be used with `query` for controlled components.
 * @param {(value: FoundValue) => void} [props.onSelectChange] - Optional handler called when a search result is selected.
 * @param {(value: FoundValue) => void} [props.onPreselectChange] - Optional handler called when a search result is preselected (e.g., hovered or focused).
 * @param {number} [props.limit=1000] - The maximum number of search results to return. Defaults to 1000.
 * @param {boolean} [props.showAll=true] - If `true`, shows all items when the query is empty. If `false`, shows no results when the query is empty.
 * @param {boolean} [props.suggest=true] - If `true`, enables suggestion mode in the search index, improving search result relevance.
 * @param {boolean} [props.lazy=false] - If `true`, defers the creation of the search index until it's needed. Useful for large datasets or when search isn't immediately required.
 * @param {boolean} [props.tabToSelect=false] - If `true`, pressing "Tab" selects the preselected item unless Shift is held down.
 * @param {FLEX_SEARCH_VERSION} [props.version=FLEX_SEARCH_VERSION.SIX] - The version of FlexSearch to use. Can be `FLEX_SEARCH_VERSION.SIX` or `FLEX_SEARCH_VERSION.SEVEN`.
 * @param {ReduceItems<Value, FoundValue>} [props.reduceItems=defaultReduceItems] - An optional function to customize the search results. Allows for result manipulation like always including certain items.
 * @param {findNextIndexType<FoundValue>} [props.findNextIndex=defaultFindNextIndex] - An optional function to determine the next index when navigating results with keyboard. Useful if item references change frequently.
 *
 * @returns {UseFuzzySearchReturn<FoundValue>} An array containing the search results, current state, and handlers.
 *
 * The returned tuple consists of:
 * - `Matches<FoundValue>`: An array of matched items, each with associated actions (`select` and `preselect`).
 * - `State<FoundValue>`: The internal state of the hook, including the query, focus state, selected, and preselected items.
 * - `Handlers`: A set of handlers for managing search input and interactions, such as `selectItem`, `preselectItem`, `unpreselectItem`, `onChange`, `onFocus`, `onBlur`, `onKeyDown`, `clear`, and `warmUpSearchIndex`.
 *
 * @example
 * ```jsx
 * import useFuzzySearch from './useFuzzySearch';
 *
 * const items = [
 *   { id: 1, name: 'Apple' },
 *   { id: 2, name: 'Banana' },
 *   { id: 3, name: 'Cherry' },
 *   // ...more items
 * ];
 *
 * function MyComponent() {
 *   const [results, state, handlers] = useFuzzySearch({
 *     items,
 *     getSearchableText: item => item.name,
 *     onSelectChange: selectedItem => console.log('Selected:', selectedItem),
 *   });
 *
 *   return (
 *     <div>
 *       <input
 *         type="text"
 *         value={state.query}
 *         onChange={handlers.onChange}
 *         onFocus={handlers.onFocus}
 *         onBlur={handlers.onBlur}
 *         onKeyDown={handlers.onKeyDown}
 *       />
 *       {state.focus && (
 *         <ul>
 *           {results.map(({ match, actions }, index) => (
 *             <li
 *               key={match.id}
 *               onClick={actions.select}
 *               onMouseEnter={actions.preselect}
 *               style={{
 *                 backgroundColor:
 *                   state.preselected === match ? 'lightgray' : 'white',
 *               }}
 *             >
 *               {match.name}
 *             </li>
 *           ))}
 *         </ul>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 *
 * @note
 * - **Controlled vs Uncontrolled Query:** If you provide the `query` prop, the hook becomes a controlled component for the query state. In this case, you should also provide `onQueryChange` to handle query updates.
 * - **Performance with Large Datasets:** Setting `lazy` to `true` can improve initial performance by deferring index creation, but may introduce a delay during the first search. Use `handlers.warmUpSearchIndex()` to manually build the index ahead of time if needed.
 * - **Customizing Results:** Use `reduceItems` to customize the search results, such as always including certain items or modifying the result structure.
 * - **Keyboard Navigation:** The `onKeyDown` handler manages keyboard interactions, including navigating results with arrow keys and selecting items with `Enter` or `Tab` (if `tabToSelect` is `true`).
 *
 * @see [FlexSearch Documentation](https://github.com/nextapps-de/flexsearch) for more details on the underlying search library.
 */
const useFuzzySearch = <Value, FoundValue = Value>({
  items,
  getSearchableText,
  search,
  query: controlledQuery,
  onQueryChange,
  onSelectChange,
  onPreselectChange,
  onUnpreselected,
  reduceItems = defaultReduceItems,
  findNextIndex = defaultFindNextIndex,
  limit = 1000,
  showAll = true,
  suggest = true,
  lazy = false,
  tabToSelect = false,
  version = FLEX_SEARCH_VERSION.SIX,
}: UseFuzzySearchProps<
  Value,
  FoundValue
>): UseFuzzySearchReturn<FoundValue> => {
  const selectChangeRef = useRef(onSelectChange);
  const preselectChangeRef = useRef(onPreselectChange);
  const unpreselectedRef = useRef(onUnpreselected);
  const queryChangeRef = useRef(onQueryChange);
  const searchableTextRef = useRef(getSearchableText);
  selectChangeRef.current = onSelectChange;
  preselectChangeRef.current = onPreselectChange;
  unpreselectedRef.current = onUnpreselected;
  queryChangeRef.current = onQueryChange;
  searchableTextRef.current = getSearchableText;

  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  const [state, dispatch] = useReducer(reducer, initialState);
  const query = controlledQuery != null ? controlledQuery : state.query;

  const [getSearchIndex, getAllIndexNumbers] = useMemo(
    function useFuzzySearch_createIndex() {
      const _getSearchIndex = once(function useFuzzySearch_getSearchIndex() {
        let index: any;
        switch (version) {
          case FLEX_SEARCH_VERSION.SEVEN: {
            index = fs7.FlexSearch.Index({tokenize: 'full'});
            break;
          }
          case FLEX_SEARCH_VERSION.SIX:
          default: {
            index = FlexSearch.create({tokenize: 'full'});
            break;
          }
        }

        let itemIndex = 0;

        for (const item of items) {
          index.add(itemIndex++, searchableTextRef.current(item));
        }

        return index;
      });

      const _getAllIndexNumbers = once(
        function useFuzzySearch_getAllIndexNumbers() {
          return Array.from(items, (_, i) => i);
        }
      );

      // If we're not being lazy, pre-warm our cache:
      if (!lazy && !search) {
        _getSearchIndex();
        _getAllIndexNumbers();
      }
      return [_getSearchIndex, _getAllIndexNumbers];
    },
    [items, lazy, version, search]
  );

  const mapItemAction = useCallback(function useFuzzySearch_mapItemAction(
    item: FoundValue
  ) {
    return new FuzzyMatch(item, dispatch as React.Dispatch<Action<FoundValue>>);
  }, []);

  // Handle when a match is selected
  useLayoutEffect(
    function useFuzzySearch_onSelectChangeEffect() {
      if (selectChangeRef.current && state.selected != null) {
        // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type 'FoundValue'.
        selectChangeRef.current(state.selected);
      }
    },
    [state.selected]
  );

  // Handle when a match is preselected
  useLayoutEffect(
    function useFuzzySearch_onPreselectChangeEffect() {
      if (preselectChangeRef.current && state.preselected != null) {
        // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type 'FoundValue'.
        preselectChangeRef.current(state.preselected);
      }

      if (unpreselectedRef.current && state.preselected == null) {
        unpreselectedRef.current();
      }
    },
    [state.preselected]
  );

  const results = useMemo(
    function useFuzzySearch_searchResult() {
      const foundIndexes =
        query === '' && showAll
          ? getAllIndexNumbers()
          : (search?.(query, limit, suggest) ??
            getSearchIndex().search({
              query,
              limit,
              suggest,
            }));

      return reduceItems(foundIndexes, toArray(items), mapItemAction);
    },
    [
      query,
      limit,
      showAll,
      reduceItems,
      mapItemAction,
      getSearchIndex,
      getAllIndexNumbers,
      items,
      suggest,
      version,
      search,
    ]
  );

  const resultsRef = useRef(results);
  resultsRef.current = results;

  // Preselect the first match when query changes
  useLayoutEffect(
    function useFuzzySearch_onSelectFirstMatchEffect() {
      const firstMatch = resultsRef.current[0]?.match || null;

      if (query !== '' && firstMatch) {
        dispatch({
          type: actionTypes.MATCH_PRESELECTED,
          payload: firstMatch,
        });
      }
    },
    [query]
  );

  const handlers = {
    /**
     * Selects the match.
     *
     * This is an alternative to the `select` method on the `ItemActions` interface. Because `select` in `ItemAction`
     * gets recreated on every render, any component you pass it to will re-render on every render. This is a stable
     * equivalent.
     */
    selectItem: useCallback(function useFuzzySearch_selectItem(
      match: FoundValue
    ) {
      dispatch({
        type: actionTypes.MATCH_SELECTED,
        payload: match,
      });
    }, []),

    /**
     * Preselects the match.
     *
     * This is an alternative to the `preselect` method on the `ItemActions` interface. Because `preselect` in `ItemAction`
     * gets recreated on every render, any component you pass it to will re-render on every render. This is a stable
     * equivalent.
     */
    preselectItem: useCallback(function useFuzzySearch_preselectItem(
      match: FoundValue
    ) {
      flushSync(() => {
        dispatch({
          type: actionTypes.MATCH_PRESELECTED,
          payload: match,
        });
      });
    }, []),

    /**
     * Unpreselects the match.
     *
     * This is an alternative to the `unpreselect` method on the `ItemActions` interface. Because `unpreselect` in `ItemAction`
     * gets recreated on every render, any component you pass it to will re-render on every render. This is a stable
     * equivalent.
     */
    unpreselectItem: useCallback(function useFuzzySearch_unpreselectItem() {
      flushSync(() => {
        dispatch({
          type: actionTypes.MATCH_PRESELECTED,
          payload: null,
        });
      });
    }, []),

    onFocus: useCallback(function useFuzzySearch_onFocus() {
      dispatch({type: actionTypes.FOCUSED});
    }, []),

    onBlur: useCallback(function useFuzzySearch_onBlur() {
      dispatch({type: actionTypes.BLURRED});
    }, []),

    onChange: useCallback(function useFuzzySearch_onChange(
      event: React.ChangeEvent<EventTarget>
    ) {
      if (queryChangeRef.current) {
        queryChangeRef.current((event.target as any).value);
      } else {
        dispatch({
          type: actionTypes.PATTERN_CHANGED,
          payload: (event.target as any).value,
        });
      }
    }, []),

    onKeyDown: useCallback(
      function useFuzzySearch_onKeyDown(
        event: React.KeyboardEvent<EventTarget>
      ) {
        switch (event.key) {
          case 'ArrowUp':
          case 'ArrowDown':
          case 'Escape':
          case 'Enter': {
            event.preventDefault();
            break;
          }
          default: {
            const metaKey = isOSX() ? event.metaKey : event.ctrlKey;
            if (metaKey && Number(event.key) >= 0 && Number(event.key) <= 9) {
              event.preventDefault();
            }
          }
        }

        dispatch({
          type: actionTypes.KEY_PRESSED,
          payload: {
            key: event.key,
            metaKey: isOSX() ? event.metaKey : event.ctrlKey,
            shiftKey: event.shiftKey,
            results: resultsRef.current,
            // @ts-expect-error - TS2322 - Type 'findNextIndexType<FoundValue>' is not assignable to type 'findNextIndexType<unknown>'.
            findNextIndex,
            tabToSelect,
          },
        });
      },
      [findNextIndex, tabToSelect]
    ),

    clear: useCallback(function useFuzzySearch_clear() {
      if (queryChangeRef.current) {
        queryChangeRef.current('');
      } else {
        dispatch({type: actionTypes.PATTERN_CHANGED, payload: ''});
      }
    }, []),

    warmUpSearchIndex: useCallback(
      function useFuzzySearch_warmUpSearchIndex() {
        if (search) return;

        // Calling this should generate the index and cache the result for
        // future calls thanks to `once(...)`:
        getSearchIndex();
      },
      [getSearchIndex, search]
    ),
  } as const;

  // @ts-expect-error - TS2322 - Type 'State<unknown>' is not assignable to type 'State<FoundValue>'.
  return [results, state, handlers];
};

const toArray = weakMemo(function toArray<Value>(
  items: Iterable<Value>
): Array<Value> {
  return Array.isArray(items) ? items : Array.from(items);
});

function isOSX() {
  const navigator = window.navigator;
  const platform = navigator.platform;
  return (
    /mac os x/.test(navigator.userAgent.toLowerCase()) ||
    platform === 'MacIntel'
  );
}

function defaultReduceItems<Value, FoundValue>(
  foundIndexes: Array<number>,
  items: Array<Value>,
  mapItemAction: (value: FoundValue) => ItemActions
): Matches<FoundValue> {
  const length = foundIndexes.length;
  const result: Matches<FoundValue> = [];
  if (length === 0) return result;

  for (let i = 0; i < length; ++i) {
    const foundIndex = foundIndexes[i];
    if (foundIndex === undefined) continue;
    const foundValue = items[foundIndex] as FoundValue;
    result.push({match: foundValue, actions: mapItemAction(foundValue)});
  }

  return result;
}

function defaultFindNextIndex<FoundValue>(
  results: Matches<FoundValue>,
  preselected: FoundValue | null
): number {
  return results.findIndex(({match}) => match === preselected);
}

class FuzzyMatch<FoundValue = null> implements ItemActions {
  match: FoundValue;
  private dispatch: React.Dispatch<Action<FoundValue>>;

  constructor(match: FoundValue, dispatch: React.Dispatch<Action<FoundValue>>) {
    this.dispatch = dispatch;
    this.match = match;
  }

  select = () => {
    this.dispatch({
      type: actionTypes.MATCH_SELECTED,
      payload: this.match,
    });
  };

  preselect = () => {
    // We force a render here because batching of events means that
    // state handlers for MATCH_PRESELECTED and MATCH_SELECTED
    // are batched which leads to the preselected state never being rendered,
    // preselection state not being rendered means that effects (selection change callbacks)
    // won't get called properly.
    flushSync(() => {
      this.dispatch({
        type: actionTypes.MATCH_PRESELECTED,
        payload: this.match,
      });
    });
  };

  unpreselect = () => {
    // We force a render here because batching of events means that
    // state handlers for MATCH_PRESELECTED and MATCH_SELECTED
    // are batched which leads to the preselected state never being rendered,
    // preselection state not being rendered means that effects (selection change callbacks)
    // won't get called properly.
    flushSync(() => {
      this.dispatch({
        type: actionTypes.MATCH_PRESELECTED,
        payload: null,
      });
    });
  };
}

export default useFuzzySearch;
